import { useEffect } from 'react';

/**
 * Hook to fix mobile viewport height issues on iOS and Android.
 *
 * Problem:
 * - iOS Safari: 100vh includes address bar, and keyboard causes layout shift
 * - Android Chrome: 100dvh shrinks when keyboard opens, pushing header out of view
 *
 * Solution:
 * When the virtual keyboard opens, visualViewport.height shrinks but the browser
 * also scrolls the page (visualViewport.offsetTop changes). By using height + offsetTop,
 * the layout maintains its full size and only the content area adjusts.
 *
 * This hook:
 * 1. Sets --app-height to the full viewport (height + offsetTop) for stable layout
 * 2. Sets --app-keyboard-offset for components that need keyboard-aware positioning
 *
 * Only runs on touch-capable devices (mobile/tablet) to avoid unnecessary work on desktop.
 */
const isTouchDevice = (): boolean => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

export const useIOSViewportFix = () => {
  useEffect(() => {
    if (!isTouchDevice()) return;

    const updateViewportHeight = () => {
      if (window.visualViewport) {
        const { height, offsetTop } = window.visualViewport;

        // Full layout height = visible height + any scroll offset from keyboard
        document.documentElement.style.setProperty(
          '--app-height',
          `${height + offsetTop}px`,
        );

        // Keyboard offset for components that need to position above the keyboard
        document.documentElement.style.setProperty(
          '--app-keyboard-offset',
          `${offsetTop}px`,
        );
      } else {
        document.documentElement.style.setProperty(
          '--app-height',
          `${window.innerHeight}px`,
        );
        document.documentElement.style.setProperty(
          '--app-keyboard-offset',
          '0px',
        );
      }
    };

    updateViewportHeight();

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateViewportHeight);
      window.visualViewport.addEventListener('scroll', updateViewportHeight);
    }

    window.addEventListener('resize', updateViewportHeight);
    window.addEventListener('orientationchange', updateViewportHeight);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          'resize',
          updateViewportHeight,
        );
        window.visualViewport.removeEventListener(
          'scroll',
          updateViewportHeight,
        );
      }
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);
    };
  }, []);
};
